import React, { useState, useMemo } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { FileText, Trash2, PlusCircle, ArrowUpRight, ArrowDownLeft, Calendar, Edit3, ArrowDownCircle, ArrowUpCircle, Truck } from "lucide-react";
import { toast } from "react-toastify";
import { useAccountMovementStore } from "../../../store/accountMovementStore";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { AccountMovement } from "../../../types/accountMovement";
import { DataTableColumn, GroupFilter } from "../../../types";
import { DespatchDialog } from "./DespatchDialog";
import { usePermission } from "../../../hooks/usePermission";
import { DespatchViewDialog } from "./DespatchViewDialog";
import { InvoiceDialog } from "../invoices/InvoiceDialog";
import { Receipt } from "lucide-react";
import { DateRangePicker } from "../../../components/DateRangePicker";

export const DespatchList = () => {
    const store = useAccountMovementStore();
    const { deleteItem } = store;
    const { handleApiError } = useApiErrorHandler();
    const { confirm, modal: confirmModal } = useConfirmModal();
    const { canCreate, canUpdate, canDelete } = usePermission('invoiceModule'); // İrsaliyeler fatura modülünden okunur (Sistemine göre revize edebilirsin)

    const [viewDespatch, setViewDespatch] = useState<AccountMovement | null>(null);
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [editDespatch, setEditDespatch] = useState<any>(null);
    const [isDespatchOpen, setIsDespatchOpen] = useState(false);
    const [despatchType, setDespatchType] = useState<'PURCHASE' | 'SALES'>('SALES');

    // Faturaya Çevirme state'leri
    const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
    const [invoiceDraft, setInvoiceDraft] = useState<any>(null);

    const [filterDates, setFilterDates] = useState<(Date | null)[] | null>(null);

    // 🌟 İrsaliyeleri filtrelemek için type: "WAYBILL" kullanıyoruz
    const despatchFilter: GroupFilter = useMemo(() => ({
        logic: "and",
        filters: [
            { field: "type", operator: "eq", value: "WAYBILL" },
        ]
    }), []);

    const combinedFilters = React.useMemo(() => {
        const filters: GroupFilter[] = [despatchFilter];
        if (filterDates && Array.isArray(filterDates) && filterDates[0] && filterDates[1]) {
            const startDate = new Date(filterDates[0]);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(filterDates[1]);
            endDate.setHours(23, 59, 59, 999);
            filters.push({
                logic: "and",
                filters: [
                    { field: "date", operator: "gte", value: startDate.toISOString() },
                    { field: "date", operator: "lte", value: endDate.toISOString() }
                ]
            });
        }
        return filters;
    }, [despatchFilter, filterDates]);

    const handleViewClick = (despatch: AccountMovement) => {
        setViewDespatch(despatch);
        setIsViewOpen(true);
    };

    const handleEditClick = (row: AccountMovement) => {
        setEditDespatch(row);
        const type = row.direction === 'OUT' ? 'SALES' : 'PURCHASE';
        setDespatchType(type);
        setIsDespatchOpen(true);
    };

    const handleOpenDespatch = (type: 'PURCHASE' | 'SALES') => {
        setEditDespatch(null);
        setDespatchType(type);
        setIsDespatchOpen(true);
    };

    const handleDeleteClick = async (despatch: AccountMovement) => {
        const ok = await confirm(`${despatch.number || 'İrsaliye'} nolu belgeyi silmek istediğinize emin misiniz? Bu işlem stok miktarlarını geri alacaktır.`);
        if (ok) {
            try {
                await deleteItem(despatch.id);
                toast.success("İrsaliye başarıyla iptal edildi ve stoklar güncellendi.");
            } catch (err) {
                handleApiError(err);
            }
        }
    };

    const handleSaveDespatch = async (payload: any) => {
        try {
            if (payload.id) {
                const despatchId = payload.id;
                const { id, ...dataWithoutId } = payload;
                await store.updateItem(despatchId, dataWithoutId);
                toast.success("İrsaliye başarıyla güncellendi.");
            } else {
                await store.createItem(payload);
                toast.success("İrsaliye başarıyla kaydedildi.");
            }
            setIsDespatchOpen(false);
            setEditDespatch(null);
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleConvertToInvoice = (row: AccountMovement) => {
        if (row.invoiceId) {
            toast.warning("Bu irsaliye zaten faturalandırılmış.");
            return;
        }
        const draft = {
            ...row,
            id: undefined, // Create olarak algılanması için ID'yi siliyoruz
            linkedWaybillIds: [row.id],
            type: "INVOICE",
            eInvoiceProfile: "EARSIVFATURA"
        };
        setInvoiceDraft(draft);
        setDespatchType(row.direction === 'OUT' ? 'SALES' : 'PURCHASE');
        setIsInvoiceOpen(true);
    };

    const handleSaveInvoice = async (payload: any) => {
        try {
            await store.createItem(payload);
            toast.success("İrsaliye başarıyla faturaya çevrildi.");
            setIsInvoiceOpen(false);
            setInvoiceDraft(null);

            // Fatura oluşturulduktan sonra irsaliyeleri tekrar fetch edebiliriz
            // ya da state üzerinde otomatik update olmasını bekleriz
        } catch (error) {
            handleApiError(error);
        }
    };

    // --- RENDER TEMPLATES ---

    const dateBodyTemplate = (row: AccountMovement) => (
        <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1.5 font-medium">
                <Calendar size={12} />
                <span>{new Date(row.date).toLocaleDateString('tr-TR')}</span>
            </div>
        </div>
    );

    const typeBodyTemplate = (row: AccountMovement) => {
        const isSales = row.direction === 'OUT';
        return (
            <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit font-bold text-[10px] border ${isSales
                ? "bg-amber-50 text-amber-700 border-amber-100"   // Sevk İrsaliyesi (Çıkış)
                : "bg-blue-50 text-blue-700 border-blue-100"      // Giriş İrsaliyesi (Alış)
                }`}>
                {isSales ? <ArrowUpRight size={12} /> : <ArrowDownLeft size={12} />}
                {isSales ? "SEVK İRSALİYESİ" : "GİRİŞ İRSALİYESİ"}
            </div>
        );
    };

    const accountBodyTemplate = (row: AccountMovement) => (
        <div className="flex flex-col">
            <span className="font-bold uppercase tracking-tight">{row.account?.name || 'Bilinmeyen Cari'}</span>
            <span className="text-[10px] font-mono">ID: #{row.accountId}</span>
        </div>
    );

    const amountBodyTemplate = (row: AccountMovement) => {
        const currency = row.currency === 'TL' ? 'TRY' : (row.currency || 'TRY');
        return (
            <div className="flex flex-col items-end gap-0.5">
                <span className="font-semibold text-xs text-slate-600">
                    {new Intl.NumberFormat('tr-TR', {
                        style: 'currency',
                        currency: currency
                    }).format(row.amount)}
                </span>
                <span className="text-[9px] text-gray-400 font-medium uppercase">
                    {row.stockMovements?.length || 0} Kalem Ürün
                </span>
            </div>
        );
    };

    const referenceBodyTemplate = (row: AccountMovement) => (
        <div className="flex flex-col">
            <div className="flex items-center gap-1">
                <span className="font-mono font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 w-fit text-xs">
                    {row.number || 'NO-REF'}
                </span>
            </div>
            {row.description && <span className="text-[10px] truncate w-32 mt-1">{row.description}</span>}
        </div>
    );

    const statusBodyTemplate = (row: AccountMovement) => {
        const isEDespatch = row.eInvoiceProfile === "EARSIVIRSALIYE" || row.eInvoiceProfile === "TEMELIRSALIYE";
        return (
            <div className="flex flex-col gap-1">
                <div className={`px-2 py-0.5 rounded text-[10px] font-bold border w-fit ${isEDespatch
                    ? "bg-purple-50 text-purple-700 border-purple-100"
                    : "bg-gray-50 text-gray-600 border-gray-200"
                    }`}>
                    {isEDespatch ? "E-İRSALİYE" : "KAĞIT İRSALİYE"}
                </div>
                {row.transporterName && (
                    <span className="text-[9px] text-slate-500 flex items-center gap-1 font-medium">
                        <Truck size={10} /> {row.transporterName}
                    </span>
                )}
            </div>
        );
    };

    const despatchColumns: DataTableColumn<AccountMovement>[] = [
        {
            field: "date",
            header: "Tarih",
            width: "140px",
            body: dateBodyTemplate,
            sortable: true
        },
        {
            field: "direction",
            header: "İrsaliye Türü",
            width: "170px",
            body: typeBodyTemplate,
            sortable: true
        },
        {
            field: "number",
            header: "İrsaliye No",
            width: "220px",
            body: referenceBodyTemplate,
            sortable: true
        },
        {
            field: "eInvoiceProfile",
            header: "Belge Türü",
            width: "170px",
            body: statusBodyTemplate,
            sortable: true
        },
        {
            field: "account.name",
            header: "Cari Hesap",
            width: "220px",
            body: accountBodyTemplate,
            sortable: true
        },
        {
            field: "amount",
            header: "Tahmini Tutar",
            width: "160px",
            align: "right",
            body: amountBodyTemplate,
            sortable: true
        },
        {
            field: "actions",
            header: "İşlemler",
            width: "180px",
            align: "center",
            body: (row) => (
                <TableActions
                    actions={[
                        {
                            icon: Receipt,
                            colorClass: "text-green-500",
                            title: "Faturaya Çevir",
                            onClick: () => handleConvertToInvoice(row),
                            show: !row.invoiceId && canCreate
                        },
                        {
                            icon: FileText,
                            colorClass: "text-blue-500",
                            title: "Görüntüle",
                            onClick: () => handleViewClick(row)
                        },
                        {
                            icon: Edit3,
                            colorClass: "text-orange-400",
                            title: "Düzenle",
                            onClick: () => handleEditClick(row),
                            show: canUpdate
                        },
                        {
                            icon: Trash2,
                            colorClass: "text-red-400",
                            title: "Sil",
                            onClick: () => handleDeleteClick(row),
                            show: canDelete
                        }
                    ]}
                />
            )
        },
    ];

    return (
        <div className="h-full">
            {confirmModal}
            <EDataTable<AccountMovement>
                tableKey="despatch_list"
                useStore={() => store}
                dataKey="id"
                title="İrsaliye Yönetimi"
                placeholder="İrsaliye No, Açıklama veya Cari Ara..."
                filters={combinedFilters}
                filterBar={
                    <div className="w-full xl:w-auto flex-shrink-0 mb-2">
                        <DateRangePicker value={filterDates as any} onChange={setFilterDates as any} />
                    </div>
                }
                searchFields={[
                    { field: "number", operator: "contains" },
                    { field: "account.name", operator: "contains" },
                    { field: "description", operator: "contains" }
                ]}
                actions={
                    canCreate ? (
                        <div className="flex gap-3">
                            <TextButton onClick={() => handleOpenDespatch('SALES')} className="bg-amber-600 text-white hover:bg-amber-700 px-4 py-2 rounded-lg flex items-center gap-2">
                                <ArrowUpCircle /> Yeni Sevk İrsaliyesi
                            </TextButton>
                            <TextButton onClick={() => handleOpenDespatch('PURCHASE')} className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg flex items-center gap-2">
                                <ArrowDownCircle /> Yeni Giriş İrsaliyesi
                            </TextButton>
                        </div>
                    ) : undefined
                }
                columns={despatchColumns}
            />

            <DespatchViewDialog
                invoice={viewDespatch}
                isOpen={isViewOpen}
                onClose={() => setIsViewOpen(false)}
                onConvertToInvoice={handleConvertToInvoice}
            />

            <DespatchDialog
                isOpen={isDespatchOpen}
                onClose={() => {
                    setIsDespatchOpen(false);
                    setEditDespatch(null);
                }}
                type={despatchType}
                initialData={editDespatch}
                onSave={handleSaveDespatch}
            />

            <InvoiceDialog
                isOpen={isInvoiceOpen}
                onClose={() => {
                    setIsInvoiceOpen(false);
                    setInvoiceDraft(null);
                }}
                type={despatchType}
                initialData={invoiceDraft}
                onSave={handleSaveInvoice}
            />
        </div>
    );
};